import {UntypedFormArray, UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {
  BaseTask,
  BillableItem,
  ClientSettings,
  County,
  CountyFeeSetting,
  CustomField,
  GenerateBillableItemForm,
  Tenant,
  WorkflowStep
} from '@ee/common/models';
import forEach from 'lodash-es/forEach';
import {BillableItemType, FlatFeeType} from '@ee/common/enums';
import {generateCourtTaskForm, GenerateCustomFieldForm, DateDefaultSettings} from '@ee/common/forms';
import {BillingFormComponent} from '@ee/common/billable-item-editor';
import {DateTime} from 'luxon';

export function GenerateStepCompletedForm(fb: UntypedFormBuilder, nextStep: WorkflowStep, county: County, tenants: Tenant[],
  countyFeeSetting: CountyFeeSetting, clientSettings: ClientSettings | null, nextCourtTask: BaseTask | null,
  forceJudgementFormCompletion: boolean = false, dateDefaults?: DateDefaultSettings) {
  const form = fb.group({
    comment: '',
    notes: '',
    document_generation: fb.array([]),
    document_templates: [],
    hidden_document_templates: [],
    custom_fields: fb.array([]),
    billable_items: fb.array([])
  });

  // set up custom fields
  if (nextStep?.custom_fields?.length) {
    forEach(nextStep?.custom_fields, (field: CustomField) => {
      (form.get('custom_fields') as UntypedFormArray).push(GenerateCustomFieldForm(fb, field, dateDefaults));
    });
  }

  // set up auto-billables
  if (nextStep?.billable_items) {
    forEach(nextStep?.billable_items, (b: BillableItem) => {
      const billableForm = generateBillables(fb, b, clientSettings, countyFeeSetting, county, tenants);
      (form.get('billable_items') as UntypedFormArray).push(billableForm);
    });
  }

  // set up court task completion form
  if (nextCourtTask && !nextCourtTask?.completed_date && (nextStep?.show_judgement_result || forceJudgementFormCompletion)) {
    const taskForm = generateCourtTaskForm(fb, nextCourtTask, 'Judgment Result',
      (nextStep?.judgement_required || forceJudgementFormCompletion));
    form.addControl('judgement_result', taskForm);
  }

  // set up court task creation form
  if (nextStep?.show_new_court_date_form && (!form.get('judgement_result') || nextStep?.new_court_date_required)) {
    let taskName = 'Court Appearance';
    if (form.get('judgement_result')) {
      taskName = 'Followup ' + taskName;
    } else {
      taskName = 'First ' + taskName;
    }

    const taskForm = generateCourtTaskForm(fb, null, taskName);
    // default due_date to today at 3pm in luxon DateTime format
    taskForm.get('due_date').setValue(DateTime.local().set({hour: 15, minute: 0}));
    form.addControl('new_court_task', taskForm);
  }

  // set up document templates that should be generated
  form.get('document_templates').setValue(nextStep?.document_templates ?? []);
  form.get('hidden_document_templates').setValue(nextStep?.hidden_document_templates ?? []);

  return form;
}

/**
 * Fee types/categories whose amount the SERVER resolves from the org's county fee settings on step
 * completion (mirrors BillableItemService's serverCalculatedFeeTypes / serverCalculatedCategoryIds).
 * A $0.00 shown in the UI for one of these does not mean $0.00 will be persisted, so they must be
 * submitted regardless of the displayed amount — the backend decides whether there's anything to bill.
 */
const SERVER_CALCULATED_FEE_TYPES: readonly FlatFeeType[] = [
  FlatFeeType.COUNTY_FEE, FlatFeeType.WRIT_FEE, FlatFeeType.SHERIFF_FEE, FlatFeeType.HEARING_FEE,
  FlatFeeType.CLIENT_ATTORNEY_FEE
];

const SERVER_CALCULATED_CATEGORY_IDS: readonly string[] = [
  'builtin-county-fee', 'builtin-writ-filing-fee', 'builtin-sheriff-fee', 'builtin-hearing-fee',
  'builtin-attorney-fee'
];

export function isServerCalculatedFee(item: { flat_fee_type?: FlatFeeType; category_id?: string } | null): boolean {
  return !!item && (
    SERVER_CALCULATED_FEE_TYPES.includes(item.flat_fee_type) ||
    SERVER_CALCULATED_CATEGORY_IDS.includes(item.category_id)
  );
}

function generateBillables(fb: UntypedFormBuilder, billableItem: BillableItem, clientSettings: ClientSettings | null, countyFee: CountyFeeSetting, county?: County,
  tenants: Tenant[] = []): UntypedFormGroup {
  let billableForm = GenerateBillableItemForm(fb, billableItem);
  const feeType = billableItem.flat_fee_type;
  const categoryId = billableItem.category_id;

  if (feeType === FlatFeeType.COUNTY_FEE || categoryId === 'builtin-county-fee') {
    billableForm = generateCountyFeeBillable(fb, countyFee, county, tenants);
  } else if (feeType === FlatFeeType.WRIT_FEE || categoryId === 'builtin-writ-filing-fee') {
    billableForm = generateWritFeeBillable(fb, countyFee, county, tenants);
  } else if (feeType === FlatFeeType.SHERIFF_FEE || categoryId === 'builtin-sheriff-fee') {
    billableForm = generateSheriffFeeBillable(fb, countyFee, county, tenants);
  } else if (feeType === FlatFeeType.HEARING_FEE || categoryId === 'builtin-hearing-fee') {
    billableForm = generateHearingFeeBillable(fb, countyFee, county, tenants);
  } else if (feeType === FlatFeeType.CLIENT_ATTORNEY_FEE || categoryId === 'builtin-attorney-fee') {
    billableForm = generateAttorneyFeeBillable(fb, billableItem, clientSettings);
  }
  if (billableItem.request_check) {
    billableForm.get('request_check')?.setValue(true);
  }
  return billableForm;
}

export function generateCountyFeeBillable(fb: UntypedFormBuilder, countyFee: CountyFeeSetting, county: County, tenants: Tenant[]): UntypedFormGroup {
  const billableForm = GenerateBillableItemForm(fb);
  billableForm.get('type').setValue(BillableItemType.FLAT_FEE);
  billableForm.get('flat_fee_type').setValue(FlatFeeType.COUNTY_FEE);
  billableForm.get('category_id')?.setValue('builtin-county-fee');
  const description = FlatFeeType.COUNTY_FEE + (county?.name ? ` - ${county?.name}` : '');
  if (countyFee) {
    billableForm.get('amount').setValue(BillingFormComponent.calculateCountyFeeAmount(countyFee, tenants) ?? 0);
  } else {
    billableForm.get('amount').setValue(0);
  }
  billableForm.get('description').setValue(description);
  if (countyFee?.county_fee_request_check) {
    billableForm.get('request_check')?.setValue(true);
    billableForm.get('check_payee_id')?.setValue(countyFee?.county_fee_payee_id ?? null);
    billableForm.get('check_payee')?.setValue(description);
  }
  return billableForm;
}

export function generateWritFeeBillable(fb: UntypedFormBuilder, countyFee: CountyFeeSetting, county: County, tenants: Tenant[]): UntypedFormGroup {
  const billableForm = GenerateBillableItemForm(fb);
  billableForm.get('type').setValue(BillableItemType.FLAT_FEE);
  billableForm.get('flat_fee_type').setValue(FlatFeeType.WRIT_FEE);
  billableForm.get('category_id')?.setValue('builtin-writ-filing-fee');
  const description = FlatFeeType.WRIT_FEE + (county?.name ? ` - ${county?.name}` : '');
  if (countyFee) {
    billableForm.get('amount').setValue(BillingFormComponent.calculateFeeAmount(countyFee.writ_fee, countyFee.writ_additional_tenant_fee, tenants) ?? 0);
  } else {
    billableForm.get('amount').setValue(0);
  }
  billableForm.get('description').setValue(description);
  if (countyFee?.writ_fee_request_check) {
    billableForm.get('request_check')?.setValue(true);
    billableForm.get('check_payee_id')?.setValue(countyFee?.writ_fee_payee_id ?? null);
    billableForm.get('check_payee')?.setValue(description);
  }
  return billableForm;
}

export function generateSheriffFeeBillable(fb: UntypedFormBuilder, countyFee: CountyFeeSetting, county: County, tenants: Tenant[]): UntypedFormGroup {
  const billableForm = GenerateBillableItemForm(fb);
  billableForm.get('type').setValue(BillableItemType.FLAT_FEE);
  billableForm.get('flat_fee_type').setValue(FlatFeeType.SHERIFF_FEE);
  billableForm.get('category_id')?.setValue('builtin-sheriff-fee');
  const description = FlatFeeType.SHERIFF_FEE + (county?.name ? ` - ${county?.name}` : '');
  if (countyFee) {
    billableForm.get('amount').setValue(BillingFormComponent.calculateFeeAmount(countyFee.sheriff_fee, countyFee.sheriff_additional_tenant_fee, tenants) ?? 0);
  } else {
    billableForm.get('amount').setValue(0);
  }
  billableForm.get('description').setValue(description);
  if (countyFee?.sheriff_fee_request_check) {
    billableForm.get('request_check')?.setValue(true);
    billableForm.get('check_payee_id')?.setValue(countyFee?.sheriff_fee_payee_id ?? null);
    billableForm.get('check_payee')?.setValue(description);
  }
  return billableForm;
}

export function generateHearingFeeBillable(fb: UntypedFormBuilder, countyFee: CountyFeeSetting, county: County, tenants: Tenant[]): UntypedFormGroup {
  const billableForm = GenerateBillableItemForm(fb);
  billableForm.get('type').setValue(BillableItemType.FLAT_FEE);
  billableForm.get('flat_fee_type').setValue(FlatFeeType.HEARING_FEE);
  billableForm.get('category_id')?.setValue('builtin-hearing-fee');
  const description = FlatFeeType.HEARING_FEE + (county?.name ? ` - ${county?.name}` : '');
  if (countyFee) {
    billableForm.get('amount').setValue(BillingFormComponent.calculateFeeAmount(countyFee.hearing_fee, countyFee.hearing_additional_tenant_fee, tenants) ?? 0);
  } else {
    billableForm.get('amount').setValue(0);
  }
  billableForm.get('description').setValue(description);
  if (countyFee?.hearing_fee_request_check) {
    billableForm.get('request_check')?.setValue(true);
    billableForm.get('check_payee_id')?.setValue(countyFee?.hearing_fee_payee_id ?? null);
    billableForm.get('check_payee')?.setValue(description);
  }
  return billableForm;
}



export function generateAttorneyFeeBillable(fb: UntypedFormBuilder, billableItem: BillableItem, clientSettings: ClientSettings | null): UntypedFormGroup {
  const billableForm = GenerateBillableItemForm(fb);
  billableForm.get('type').setValue(BillableItemType.FLAT_FEE);
  billableForm.get('flat_fee_type').setValue(FlatFeeType.CLIENT_ATTORNEY_FEE);
  if (clientSettings?.attorney_fee) {
    billableForm.get('amount').setValue(clientSettings.attorney_fee);
    billableForm.get('description').setValue(billableItem.description);
  } else {
    billableForm.get('amount').setValue(0);
    billableForm.get('description').setValue('Attorney Fee');
  }
  return billableForm;
}
